import { Environment, LeagueConfig } from '@leagueplatform/core';
import { BrowserRouter } from 'react-router-dom';
import { ReactQueryDevtools } from 'react-query/devtools';
import React, { Suspense } from 'react';
import {
  ERROR_TYPES,
  ErrorState,
  FirebaseActivatorContainer,
  initMasonryRegistry,
  LoadingSpinner,
  useGetAuthConfig,
  useInjectDynatraceScript,
  QueryClientProvider,
  LeagueProvider,
  ErrorBoundary,
} from '@highmark-web/highmark-common';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { AppRoutes } from 'routes/routes';
import { initializeNonConfigSDKs } from './config/init-non-config-sdks';
import { getLeagueStaticConfig } from './config/league-static-config';
import { STANDALONE_AUTH_CONFIG } from './config/standalone-auth-config';
import { FIREBASE_ACTIVATE_CONFIG } from './common/firebase/firebase';
import { initAppMasonryRegistry } from './utils/init-app-masonry-registry';

initializeNonConfigSDKs();
initMasonryRegistry();
initAppMasonryRegistry();

export function App() {
  const { isOnline, isOffline } = useOnlineStatus();

  useInjectDynatraceScript(
    import.meta.env.VITE_DYNATRACE_APP_ID,
    import.meta.env.VITE_ENV as Environment,
  );

  const { isLoading: isAuthLoading, config: authConfig } = useGetAuthConfig(
    STANDALONE_AUTH_CONFIG,
  );
  const config: LeagueConfig = getLeagueStaticConfig(authConfig);

  if (isAuthLoading) {
    return <LoadingSpinner />;
  }

  return (
    <BrowserRouter>
      <QueryClientProvider>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FirebaseActivatorContainer {...FIREBASE_ACTIVATE_CONFIG} />
        <LeagueProvider config={config} treatConfigAsInitialOnly>
          <ErrorBoundary contextLocation="Root App ErrorBoundary">
            <Suspense fallback={<LoadingSpinner />}>
              {isOffline && (
                <ErrorState
                  error={null}
                  isFullPage
                  errorType={ERROR_TYPES.NO_CONNECTION}
                />
              )}
              {isOnline && <AppRoutes />}
            </Suspense>
            <ReactQueryDevtools />
          </ErrorBoundary>
        </LeagueProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
